import {
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import React, { useCallback, useState } from "react";
import { QuoteApi } from "../../api/QuoteApi";
import useQuote from "../../hooks/useQuote";
import { UserRole } from "../../lib/models/enums";
import { QuoteFormActions } from "../../lib/types/QuoteFormActions";
import { isElevatedUser } from "../../lib/UserPermissions";
import DotsVerticalIcon from "../icons/DotsVerticalIcon";
import ShowForRole from "../ShowForRole";
import WarningDialog from "../WarningDialog";

export type FormSubmitButtonProps = {
  handler: (action: QuoteFormActions, values: any) => void;
  role: UserRole | null;
};

function FormSubmitButton({ handler, role }: FormSubmitButtonProps) {
  const quoteContext = useQuote();
  const { values, isValid } = useFormikContext<any>();
  const { state, isOwned, quoteId } = quoteContext;
  const { sfIntegration, isSubmitting, metadata } = state;
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState<boolean>(false);
  const toast = useToast();
  const toastVariant = useColorModeValue("subtle", "solid");

  const quoteHandler = useCallback(
    (action: QuoteFormActions) => {
      handler(action, values);
    },
    [handler, values]
  );

  const canSubmitQuote = React.useMemo(() => {
    return sfIntegration?.sfaccountid && sfIntegration?.sfdealid;
  }, [sfIntegration]);

  const commonDisable =
    state.isNewQuote ||
    !isValid ||
    !state.hasCalculated ||
    state.hasCalculationErrors ||
    state.hasFormErrors;

  const disableSubmit = commonDisable || !canSubmitQuote;

  const handleSalesFinalizedClick = () => {
    setIsSubmitDialogOpen(true);
  };
  const handleConfirm = () => {
    setIsSubmitDialogOpen(!isSubmitDialogOpen);
    handleSubmitSalesFinalized();
  };

  const handleWarningDialogClose = () => {
    setIsSubmitDialogOpen(false);
  };

  const handleSubmitSalesFinalized = useCallback(() => {
    if (!quoteId) return;

    QuoteApi.submitSalesFinalized(quoteId)
      .then((res) => {
        if (res.data) {
          toast({
            title: "Quote is Sales Finalized",
            description: "The quote has been sales finalized.",
            status: "success",
            variant: toastVariant,
            isClosable: true,
            position: "top-right",
          });
        }
      })
      .catch((err) => {
        toast({
          title: "Quote Not Finalized",
          description:
            "An error has occurred when attempting to finalize your quote.",
          status: "error",
          variant: toastVariant,
          isClosable: true,
          position: "top-right",
        });
      })
      .finally(() => quoteHandler("SAVE"));
  }, [quoteId, quoteHandler, toast, toastVariant]);

  return (
    <>
      <ShowForRole
        roles={[
          "Sales",
          "Broker",
          "Broker Manager",
          "Lender Finance",
          "Lender Finance Manager",
          "Inside Sales",
          "Inside Sales Manager",
          "Wholesale",
          "Wholesale Manager",
          "Business Development",
          "Capital Markets",
          "POD",
          "Business Development Manager",
          "Production Coordinator",
          "Operations Analyst",
          "Sales Assistant",
          "Administrator",
        ]}
      >
        <Menu>
          <ButtonGroup width="full" isAttached marginTop={2} variant="brand">
            <Button
              width="full"
              onClick={() => quoteHandler("SUBMIT")}
              isLoading={isSubmitting}
              loadingText="Submitting..."
              isDisabled={
                disableSubmit ||
                metadata?.underwriterStatus === "Sales Finalized" ||
                metadata?.underwriterStatus === "Final" ||
                !(isOwned || isElevatedUser(role))
              }
            >
              {state.requiresQuoteResubmission
                ? "Resubmit Quote"
                : "Submit Quote"}
            </Button>
            {state.hasQuoteBeenSubmitted && (
              <MenuButton
                as={IconButton}
                icon={<DotsVerticalIcon h={6} w={6} />}
                borderLeft="1px"
                borderLeftColor="gray.400"
                isDisabled={
                  disableSubmit ||
                  metadata?.underwriterStatus === "Sales Finalized" ||
                  metadata?.underwriterStatus === "Final" ||
                  !(isOwned || isElevatedUser(role))
                }
              />
            )}
          </ButtonGroup>

          <MenuList>
            <MenuItem onClick={() => handleSalesFinalizedClick()}>
              Sales Finalized
            </MenuItem>
          </MenuList>
        </Menu>
      </ShowForRole>
      <ShowForRole roles={["External"]} hideForAdmin>
        <Button
          variant="brand"
          width="full"
          onClick={() => quoteHandler("SUBMIT_FOR_REVIEW")}
          isLoading={isSubmitting}
          loadingText="Submitting..."
          isDisabled={
            commonDisable ||
            !isOwned ||
            metadata?.underwriterStatus !== "Not Submitted"
          }
          marginTop={2}
        >
          {state.requiresQuoteResubmission
            ? "Resubmit For Review"
            : "Submit For Review"}
        </Button>
      </ShowForRole>
      <WarningDialog
        confirmText="Submit"
        contentText={`Are you sure you want to set the underwriter status to "Sales Finalized"?`}
        isOpen={isSubmitDialogOpen}
        titleText="Sales Finalized?"
        danger={false}
        onConfirmHandler={() => handleConfirm()}
        onCloseHandler={handleWarningDialogClose}
      />
    </>
  );
}

export default FormSubmitButton;
